import { Helmet } from "react-helmet-async";
import { Layout } from "@/components/layout/Layout";
import { Mail, Phone, MapPin, Clock } from "lucide-react";
import { motion } from "framer-motion";
import MultiStepForm from "@/components/contact/MultiStepForm";
import { useBusinessSettings } from "@/hooks/useBusinessSettings";

const CAL_LINK = "https://cal.com/pixiecliks/30min";

const Contact = () => {
  const { getSetting } = useBusinessSettings();
  
  const email = getSetting("email");
  const phone = getSetting("phone");
  const phoneDisplay = getSetting("phone_display");
  const serviceAreas = getSetting("service_areas");
  const businessHours = getSetting("business_hours");
  return (
    <>
      <Helmet>
        <title>Contact Pixie Clicks | Check Your Wedding Date | NJ, FL & CO</title>
        <meta
          name="description"
          content="Check your wedding date availability and get a custom quote. Photography, video & photo booth services in NJ, FL & CO."
        />
        <meta property="og:title" content="Contact Pixie Clicks | Check Your Wedding Date" />
        <meta property="og:description" content="Check your wedding date availability and get a custom quote for photography, video, or photo booth." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://pixieclicks.com/contact" />
        <link rel="canonical" href="https://pixieclicks.com/contact" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ContactPage",
            "name": "Contact Pixie Clicks",
            "description": "Check your wedding date availability and get a custom quote.",
            "mainEntity": {
              "@type": "LocalBusiness",
              "name": "Pixie Clicks",
              "areaServed": ["New Jersey", "Florida", "Colorado"]
            }
          })}
        </script>
      </Helmet>
      <Layout>
        <section className="section-padding">
          <div className="container-wide">
            <div className="grid gap-12 lg:grid-cols-5">
              {/* Left sidebar - Contact info */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="lg:col-span-2"
              >
                <h1 className="font-serif text-4xl font-semibold text-foreground md:text-5xl">
                  Let's Create{" "}
                  <span className="text-primary">Magic Together</span>
                </h1>
                <p className="mt-6 text-lg text-muted-foreground">
                  Check your date, get a personalized brochure, and schedule a
                  consultation call — all in just a few clicks.
                </p>
                
                <div className="mt-10 space-y-6">
                  <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                      <Mail className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">Email</p>
                      <a
                        href={`mailto:${email}`}
                        className="text-muted-foreground hover:text-primary transition-colors"
                      >
                        {email}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                      <Phone className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">Phone</p>
                      <a
                        href={`tel:${phone}`}
                        className="text-muted-foreground hover:text-primary transition-colors"
                      >
                        {phoneDisplay}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                      <MapPin className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">Service Areas</p>
                      <p className="text-muted-foreground">
                        {serviceAreas}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                      <Clock className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">Business Hours</p>
                      <p className="text-muted-foreground">
                        {businessHours}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Social proof */}
                <div className="mt-10 rounded-xl bg-muted/30 p-6 border border-border">
                  <p className="text-sm font-medium text-foreground mb-2">
                    Trusted by 500+ couples
                  </p>
                  <p className="text-sm text-muted-foreground italic">
                    "From the first inquiry to our wedding day, Pixie Clicks made everything so easy and fun!"
                  </p>
                  <p className="text-xs text-muted-foreground mt-2">
                    — Sarah & Michael, NJ Wedding
                  </p>
                </div>
              </motion.div>

              {/* Right side - Multi-step form */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                className="lg:col-span-3"
              >
                <MultiStepForm calLink={CAL_LINK} />
              </motion.div>
            </div>
          </div>
        </section>
      </Layout>
    </>
  );
};

export default Contact;
